import React, { Component, ReactNode } from 'react';
import { RootState } from '../../redux/store';
import { GraphState } from '../../redux/reducers/GraphReducer';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import Icon from '../../icons/Icon';
import CustomLoading from '../custom/CustomLoading';
import { Graph, GraphArgs, GraphNode } from '@trlogic/ui-pack';
import { createPortal } from 'react-dom';
import GraphNodeInformationComponent from './GraphNodeInformationComponent';
import DataType from '../../constant/DataType';
import { changePageFilter } from '../../redux/actions/PageActions';
import {
  changeGraphFields,
  changeGraphLoading,
  changeInformationCoordinates,
  changeInformationVisible,
  changeLoading,
  changeNodeLimit,
  changeResponse,
  changeSelectedFields,
  changeSelectedNode,
  changeSelectedTransaction,
  changeTransactions,
} from '../../redux/actions/GraphActions';
import { GraphInformationJsonModel, GraphNodeInformationConfiguration } from '../../domain/graph/GraphDomains';
import CaseService from '../../service/CaseService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { query } from '@trlogic/client-js';
import Filter = query.Filter;
import ConditionalFilter = query.ConditionalFilter;
import { CustomFilterField } from 'domain/ui/FilterDomains';
import LanguageUtil from '../../util/LanguageUtil';

class GraphExecutorComponent extends Component<Props> {
  private caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);

  public render() {
    const {
      graph: { response, isGraphLoading, caseGraphFields, selectedNode, update, informationCoordinates, informationVisible },
    } = this.props;
    if (response == undefined || response.nodes.length == 0 || response.links.length == 0 || response == '') return this.getNoDataMessage();
    if (isGraphLoading) return <CustomLoading />;
    return (
      <div className={'flex flex-col w-full max-h-[calc(100dvh-320px)] overflow-hidden h-full items-center justify-center relative'}>
        <Graph
          data={response}
          args={
            {
              onClickNode: this.onClickNode as any,
              width: 1600,
              forceStrength: -500,
              forceDistance: 50,
            } as GraphArgs
          }
          key={update}
        />
        {informationVisible &&
          selectedNode &&
          caseGraphFields &&
          createPortal(
            <GraphNodeInformationComponent
              configurationName={caseGraphFields._id}
              visible={informationVisible}
              onChangeVisible={this.onChangeInformationVisible}
              coordinates={informationCoordinates}
              onChangeFilter={this.addFilter}
              currencyConfiguration={this.props.graph.caseGraphFields?.transactionCurrencyField}
              nodeInformationJsonModel={selectedNode}
            />,
            document.body,
          )}
      </div>
    );
  }

  private getNoDataMessage = (): ReactNode => {
    return (
      <div className={'flex flex-col w-full h-full items-center justify-center'}>
        <Icon icon={'no-folder'} size={100} className={'text-f-black-2'} />
        <span className={'text-f-black-2 font-bold text-lg'}>{LanguageUtil.get({ id: 'common.label.noData', defaultMessage: 'No Data' })}</span>
      </div>
    );
  };

  private addFilter = async (filterItem: any) => {
    const { changePageFilter, pageFilter, onChangeFilter, filter } = this.props;
    const request = this.prepareRequest();
    const _filter: ConditionalFilter = {
      filterType: 'ConditionalFilter',
      type: 'and',
      filters: [],
    };
    request.filter = _filter;
    filterItem?.filters?.forEach((f: any) => {
      if (filter.filterType != 'EmptyFilter') {
        filter?.filters.push(f);
      } else {
        _filter.filters.push(f);
        onChangeFilter(_filter);
      }
      pageFilter.filter.fields.push({
        field: f.key,
        label: f.key.split('.')[1],
        search: {
          type: 'LITERAL',
          value: {
            operator: 'eq',
            properties: {
              dataType: DataType.STRING,
            },
            value: f.value,
          },
        },
        enabled: true,
      } as CustomFilterField);
    });
    changePageFilter(pageFilter);
  };

  private prepareRequest = (): any => {
    const {
      graph: { selectedTransaction, selectedFields, nodeLimit },
    } = this.props;
    return {
      transaction: selectedTransaction,
      defaultFilterKeys: [],
      groups: selectedFields?.map((item) => ({ label: item.label, field: item.value, icon: item.icon })),
      nodeLimit: nodeLimit,
      filter: this.props.filter,
    };
  };

  private onClickNode = async (event: any, node: GraphNode) => {
    const { changeInformationCoordinates, changeSelectedNode, changeInformationVisible } = this.props;
    const { x, y } = event.client;
    let _x = x,
      _y = event.client.y;
    if (500 - y < 0) {
      _y -= 230;
    }
    if (x + 500 > window.innerWidth) {
      _x -= 500;
    }
    changeInformationCoordinates({ x: _x, y: _y });
    changeInformationVisible(true);
    const nodeInformationRequest: GraphNodeInformationConfiguration = {
      transaction: this.props.graph.selectedTransaction,
      key: node.key?.split('-')[0],
      transactionAmountField: this.props.graph.caseGraphFields?.transactionAmountField as string,
      filter: {
        filterType: 'ConditionalFilter',
        type: 'and',
        filters: [
          {
            filterType: 'MatchFilter',
            key: 'variables.' + node.key?.split('-')[0] + '.keyword',
            value: node.label,
            type: 'eq',
          },
        ],
      } as Filter,
    };
    const response = await this.caseService.getNodeInformation(nodeInformationRequest);

    const nodeInformation: GraphInformationJsonModel = {
      nodeInfo: response,
      node: node as any,
    };
    changeSelectedNode(nodeInformation);
  };

  private onChangeInformationVisible = (visible: boolean) => {
    this.props.changeInformationVisible(visible);
  };
}

interface Props extends ReduxProps {
  filter: any;
  onChangeFilter: (filter: any) => void;
  isCasePage?: boolean;
}

const mapStateToProps = (state: RootState) => ({
  pageFilter: state.page.filter,
  graph: state.graph as GraphState,
});
const mapDispatchToProps = {
  changePageFilter,
  changeGraphFields,
  changeTransactions,
  changeGraphLoading,
  changeLoading,
  changeSelectedTransaction,
  changeNodeLimit,
  changeResponse,
  changeSelectedNode,
  changeInformationVisible,
  changeInformationCoordinates,
  changeSelectedFields,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(GraphExecutorComponent);
